import type { ReactElement } from "react";

import {
  UNSTABLE_Toast,
  UNSTABLE_ToastContent,
  UNSTABLE_ToastRegion,
  UNSTABLE_ToastQueue,
} from "react-aria-components";
import { flushSync } from "react-dom";

import { Alert } from "#components/Alert";
import { cn } from "#utils";

import "./toast.css";

import type { AlertSeverity } from "#components/Alert";

/** Content shape for a toast notification */
export interface ToastContent {
  /** The message to display - can be a single string or array of strings */
  message: string | string[];
  /** The severity level determining color and icon */
  severity: AlertSeverity;
  /** Auto-dismiss duration in ms (minimum 5000 for accessibility) */
  duration?: number;
}

/**
 * ToastQueue wraps UNSTABLE_ToastQueue with CSS View Transition support
 * so that toasts animate in and out smoothly.
 */
export class ToastQueue<T> extends UNSTABLE_ToastQueue<T> {
  constructor(options?: { maxVisibleToasts?: number }) {
    super({
      ...options,
      wrapUpdate(fn) {
        if ("startViewTransition" in document) {
          document.startViewTransition(() => {
            flushSync(fn);
          });
        } else {
          fn();
        }
      },
    });
  }
}

/** ToastRegionProps interface for the ToastRegion component */
export interface ToastRegionProps {
  /** The toast queue instance to render */
  queue: InstanceType<typeof UNSTABLE_ToastQueue<ToastContent>>;
  /** Additional CSS classes for the region container */
  className?: string;
}

/** ToastRegion renders toast notifications at the top-center of the viewport */
export function ToastRegion({ queue, className }: ToastRegionProps): ReactElement | null {
  return (
    <UNSTABLE_ToastRegion
      className={cn(
        "fixed left-1/2 top-4 z-[9999] flex -translate-x-1/2 flex-col gap-2",
        className,
      )}
      queue={queue}
    >
      {({ toast }) => (
        <UNSTABLE_Toast
          className="toast-item min-w-80 max-w-lg"
          style={{ viewTransitionName: toast.key }}
          toast={toast}
        >
          <UNSTABLE_ToastContent>
            <Alert
              onClose={() => queue.close(toast.key)}
              role="none"
              severity={toast.content.severity}
            >
              {Array.isArray(toast.content.message) ? (
                <ul className="m-0 list-inside list-disc p-0">
                  {toast.content.message.map((msg, index) => (
                    <li key={index}>{msg}</li>
                  ))}
                </ul>
              ) : (
                toast.content.message
              )}
            </Alert>
          </UNSTABLE_ToastContent>
        </UNSTABLE_Toast>
      )}
    </UNSTABLE_ToastRegion>
  );
}
